import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";

interface BankDataItem {
  id: string;
  depositdate?: Timestamp | null;
  name: string;
  outcash: number;
  incash: number;
  orderNumber: string;
  displayName: string;
  check: boolean;
}

interface PaymentItem {
  id: string;
  orderId: string;
  orderRefPath: string;
  dueDate?: Timestamp | null;
  amount: number;
  installmentNumber?: number;
  paymentMethod: string;
  userName: string;
  userId: string;
  orderNumber: string;
  paymentRefPath: string;
  orderTrouble: boolean;
}

const formatDate = (value?: Timestamp | null) => {
  if (!value) return "-";
  return value.toDate().toLocaleDateString("ja-JP");
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("ja-JP");
};

const getProjectId = (target: {
  app?: { options?: { projectId?: string } };
  firestore?: { app?: { options?: { projectId?: string } } };
}) => target.app?.options?.projectId ?? target.firestore?.app?.options?.projectId;

const isSameProjectReference = (ref: { firestore?: { app?: { options?: { projectId?: string } } } }) => {
  const refProjectId = getProjectId(ref);
  const currentProjectId = getProjectId(db);
  if (!refProjectId || !currentProjectId) return true;
  return refProjectId === currentProjectId;
};

export default function PaymentManagementPage() {
  const [bankData, setBankData] = useState<BankDataItem[]>([]);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [selectedBankIds, setSelectedBankIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [excludingId, setExcludingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingPayment, setConfirmingPayment] = useState<PaymentItem | null>(null);
  const [paymentTab, setPaymentTab] = useState<"normal" | "trouble">("normal");
  const [adminMode, setAdminMode] = useState(false);

  const fetchBankData = useCallback(async () => {
    const bankQuery = query(collection(db, "bankdata"), orderBy("depositdate", "asc"));
    const snapshot = await getDocs(bankQuery);
    const data = snapshot.docs.map((docSnap) => {
      const docData = docSnap.data() as Omit<BankDataItem, "id">;
      return {
        id: docSnap.id,
        depositdate: docData.depositdate ?? null,
        name: docData.name || "-",
        outcash: docData.outcash ?? 0,
        incash: docData.incash ?? 0,
        orderNumber: docData.orderNumber || "-",
        displayName: docData.display_name || docData.displayName || "-",
        check: Boolean(docData.check),
      };
    });
    setBankData(data);
  }, []);

  const fetchPayments = useCallback(async () => {
    const paymentsQuery = query(collectionGroup(db, "payments"), where("paycheck", "==", false));
    const snapshot = await getDocs(paymentsQuery);
    const paymentRows = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const paymentData = docSnap.data() as Record<string, any>;
        const orderRef = docSnap.ref.parent.parent;
        let orderNumber = "-";
        let userName = "-";
        let paymentMethod = "-";
        let orderId = "-";
        let userId = "-";
        let orderTrouble = false;
        if (orderRef) {
          orderId = orderRef.id;
          try {
            const orderSnap = await getDoc(orderRef);
            if (orderSnap.exists()) {
              const orderData = orderSnap.data() as Record<string, any>;
              if (orderData.lost || orderData.status === "cancell") {
                return null;
              }
              orderTrouble = Boolean(orderData.trouble);
              orderNumber = orderData.orderNumber || "-";
              paymentMethod = orderData.paymentMethod || "-";
              const userRef = orderData.UserRef || orderData.userId;
              if (userRef) {
                try {
                  const resolvedRef = userRef.path ? userRef : doc(db, "User", userRef);
                  if (!isSameProjectReference(resolvedRef)) {
                    console.warn("Skipped user reference from another project", resolvedRef.path);
                  } else {
                    userId = resolvedRef.id;
                    const userSnap = await getDoc(resolvedRef);
                    if (userSnap.exists()) {
                      const userData = userSnap.data() as Record<string, any>;
                      userName = userData.display_name || userData.display_Name || userSnap.id;
                    }
                  }
                } catch (userError) {
                  console.error("Failed to load user", userError);
                }
              }
            }
          } catch (orderError) {
            console.error("Failed to load order", orderError);
          }
        }
        return {
          id: docSnap.id,
          orderId,
          orderRefPath: orderRef?.path || "",
          dueDate: paymentData.dueDate ?? null,
          amount: paymentData.amount ?? 0,
          installmentNumber: paymentData.installmentNumber,
          paymentMethod,
          userName,
          userId,
          orderNumber,
          paymentRefPath: docSnap.ref.path,
          orderTrouble,
        } satisfies PaymentItem;
      })
    );
    const normalizedPayments = paymentRows.filter(Boolean) as PaymentItem[];
    const sortedPayments = normalizedPayments.sort((a, b) => {
      const aTime = a.dueDate?.toMillis?.() ?? 0;
      const bTime = b.dueDate?.toMillis?.() ?? 0;
      return aTime - bTime;
    });
    setPayments(sortedPayments);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchBankData(), fetchPayments()]);
    } catch (fetchError: any) {
      const errorCode = fetchError?.code || fetchError?.name;
      if (errorCode === "permission-denied") {
        console.error("Permission error while loading payment management data", fetchError);
        setError("権限エラーのため入金管理データの取得に失敗しました。");
      } else {
        console.error("Failed to load payment management data", fetchError);
        setError("入金管理データの取得に失敗しました。");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchBankData, fetchPayments]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!confirm("スプレッドシートから同期しますか?\n同期済みの行はスキップされます。")) {
      return;
    }
    setSyncing(true);
    try {
      const syncFunction = httpsCallable(functions, "syncBankdataFromSheet");
      await syncFunction();
      await fetchBankData();
    } catch (syncError: any) {
      console.error("Failed to sync bankdata", syncError);
      alert(syncError?.message || "同期に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  const visibleBankData = useMemo(() => bankData.filter((item) => !item.check), [bankData]);

  const selectedBankData = useMemo(
    () => visibleBankData.filter((item) => selectedBankIds.has(item.id)),
    [visibleBankData, selectedBankIds]
  );

  const handleToggleBankSelection = (id: string, checked: boolean) => {
    setSelectedBankIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleClearSelected = () => {
    setSelectedBankIds(new Set());
  };

  const handleExcludeBankItem = async (item: BankDataItem) => {
    if (!selectedBankIds.has(item.id)) return;
    if (!confirm("選択した入金データを対象外にしますか?")) return;
    setExcludingId(item.id);
    try {
      await updateDoc(doc(db, "bankdata", item.id), { check: true });
      setBankData((prev) =>
        prev.map((entry) => (entry.id === item.id ? { ...entry, check: true } : entry))
      );
      setSelectedBankIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } catch (excludeError: any) {
      console.error("Failed to exclude bankdata item", excludeError);
      alert(excludeError?.message || "対象外の更新に失敗しました。");
    } finally {
      setExcludingId(null);
    }
  };

  const requestMatchPayment = (payment: PaymentItem) => {
    if (selectedBankData.length === 0) {
      alert("左側で入金データを選択してください。");
      return;
    }
    setConfirmingPayment(payment);
  };

  const handleConfirmMatch = async () => {
    if (!confirmingPayment) return;

    setProcessing(true);
    try {
      if (!confirmingPayment.orderId || confirmingPayment.orderId === "-") {
        throw new Error("消込対象の注文情報が特定できませんでした。画面を再読み込みして再実行してください。");
      }

      const orderRefFromId = doc(db, "Order", confirmingPayment.orderId);
      const paymentRefFromIds = doc(
        db,
        "Order",
        confirmingPayment.orderId,
        "payments",
        confirmingPayment.id
      );

      if (
        confirmingPayment.paymentRefPath &&
        confirmingPayment.paymentRefPath !== paymentRefFromIds.path
      ) {
        throw new Error(
          "選択したpaymentsの参照先が不整合です。画面を再読み込みして、対象を選び直してください。"
        );
      }

      const bankNames = selectedBankData
        .map((item) => item.name)
        .filter((name) => Boolean(name && name !== "-"))
        .join(" / ");
      const totalIncash = selectedBankData.reduce((sum, item) => sum + (item.incash || 0), 0);
      const depositDates = selectedBankData
        .map((item) => item.depositdate)
        .filter((date): date is Timestamp => Boolean(date));
      const paidAtValue =
        depositDates.length > 0
          ? depositDates.reduce((latest, current) =>
              current.toMillis() > latest.toMillis() ? current : latest
            )
          : serverTimestamp();
      await runTransaction(db, async (transaction) => {
        const paymentSnap = await transaction.get(paymentRefFromIds);
        if (!paymentSnap.exists()) {
          throw new Error("選択したpaymentsが見つかりません。画面を再読み込みして再実行してください。");
        }
        if (Boolean(paymentSnap.data()?.paycheck)) {
          throw new Error("選択したpaymentsはすでに消込済みです。最新状態を確認してください。");
        }

        const bankRefs = selectedBankData.map((bankItem) => doc(db, "bankdata", bankItem.id));
        const bankSnapshots = await Promise.all(bankRefs.map((bankRef) => transaction.get(bankRef)));

        bankSnapshots.forEach((bankSnap, index) => {
          const bankItem = selectedBankData[index];
          if (!bankSnap.exists()) {
            throw new Error(`bankdata(${bankItem.id})が見つかりません。`);
          }
          if (Boolean(bankSnap.data()?.check)) {
            throw new Error("選択したbankdataに消込済みデータが含まれています。最新状態を確認してください。");
          }
        });

        transaction.update(paymentRefFromIds, {
          deposit: totalIncash,
          paycheck: true,
          paidAt: paidAtValue,
          name: bankNames || "-",
        });

        selectedBankData.forEach((bankItem, index) => {
          const bankRef = bankRefs[index];
          transaction.update(bankRef, {
            check: true,
            paymentsRef: paymentRefFromIds,
            OrderRef: orderRefFromId,
            orderNumber: confirmingPayment.orderNumber || "-",
            userName: confirmingPayment.userName || "-",
          });
        });
      });
      setBankData((prev) =>
        prev.map((entry) =>
          selectedBankIds.has(entry.id) ? { ...entry, check: true } : entry
        )
      );

      await updateOrderPaymentStatus(confirmingPayment.orderRefPath);

      handleClearSelected();
      await fetchData();
    } catch (processError: any) {
      console.error("Failed to match payment", processError);
      alert(processError?.message || "消込に失敗しました。");
    } finally {
      setProcessing(false);
      setConfirmingPayment(null);
    }
  };

  const updateOrderPaymentStatus = useCallback(async (orderRefPath?: string) => {
    if (!orderRefPath) return;

    const orderPaymentsSnapshot = await getDocs(query(collection(db, orderRefPath, "payments")));
    const allChecked =
      orderPaymentsSnapshot.docs.length > 0 &&
      orderPaymentsSnapshot.docs.every((docSnap) => Boolean(docSnap.data().paycheck));
    const orderUpdates: Record<string, boolean> = { paymentstatus: allChecked };
    if (allChecked) {
      orderUpdates.trouble = false;
    }
    await updateDoc(doc(db, orderRefPath), orderUpdates);

    // 同じ orderNumber を持つ全 Order に total_paymentstatus を伝播
    const orderSnap = await getDoc(doc(db, orderRefPath));
    const orderNumber = orderSnap.data()?.orderNumber;
    if (orderNumber) {
      const siblingsSnap = await getDocs(query(collection(db, "Order"), where("orderNumber", "==", orderNumber)));
      const allStatuses = siblingsSnap.docs.map((sibDoc) => {
        if (sibDoc.ref.path === orderRefPath) return allChecked;
        return Boolean(sibDoc.data().paymentstatus);
      });
      const total_paymentstatus = allStatuses.length > 0 && allStatuses.every(Boolean);
      await Promise.all(
        siblingsSnap.docs.map((sibDoc) => updateDoc(sibDoc.ref, { total_paymentstatus }))
      );
    }
  }, []);

  const handleKmjMatch = async (payment: PaymentItem) => {
    if (payment.paymentMethod !== "コンビニ決済") return;
    if (!confirm(`「${payment.orderNumber}」をKMJ消込しますか？`)) return;

    setProcessing(true);
    try {
      if (!payment.orderId || payment.orderId === "-") {
        throw new Error("消込対象の注文情報が特定できませんでした。画面を再読み込みして再実行してください。");
      }

      const paymentRef = doc(db, "Order", payment.orderId, "payments", payment.id);
      if (payment.paymentRefPath && payment.paymentRefPath !== paymentRef.path) {
        throw new Error(
          "選択したpaymentsの参照先が不整合です。画面を再読み込みして、対象を選び直してください。"
        );
      }

      await updateDoc(paymentRef, {
        deposit: payment.amount ?? 0,
        paidAt: serverTimestamp(),
        paycheck: true,
      });

      await updateOrderPaymentStatus(payment.orderRefPath);
      await fetchData();
    } catch (kmjError: any) {
      console.error("Failed to process KMJ match", kmjError);
      alert(kmjError?.message || "KMJ消込に失敗しました。");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeletePayment = async (payment: PaymentItem) => {
    if (!adminMode) return;
    if (!confirm(`「${payment.orderNumber}」のpaymentを削除しますか？`)) return;
    setProcessing(true);
    try {
      await deleteDoc(doc(db, payment.paymentRefPath));
      await fetchData();
    } catch (deleteError: any) {
      console.error("Failed to delete payment", deleteError);
      alert(deleteError?.message || "paymentの削除に失敗しました。");
    } finally {
      setProcessing(false);
    }
  };

  const filteredPayments = useMemo(
    () =>
      payments.filter((payment) =>
        paymentTab === "trouble" ? payment.orderTrouble : !payment.orderTrouble
      ),
    [paymentTab, payments]
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">入金管理</h2>
          <p className="text-sm text-gray-500">bankdata と payments の消込を行います。</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/paymentmg/bankdata"
            className="px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
          >
            入金履歴一覧
          </Link>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              checked={adminMode}
              onChange={(event) => setAdminMode(event.target.checked)}
            />
            管理モード
          </label>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {syncing ? "同期中..." : "スプレッドシート同期"}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-500">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">入金履歴</h3>
            <span className="text-xs text-gray-500">選択: {selectedBankIds.size}件</span>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-gray-500">読み込み中...</div>
          ) : visibleBankData.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">入金データがありません。</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-emerald-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">選択</th>
                    <th className="px-3 py-2 text-left">日付</th>
                    <th className="px-3 py-2 text-left">名義</th>
                    <th className="px-3 py-2 text-right">入金</th>
                    <th className="px-3 py-2 text-center">対象外</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleBankData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 disabled:opacity-50"
                          checked={selectedBankIds.has(item.id)}
                          onChange={(event) => handleToggleBankSelection(item.id, event.target.checked)}
                          disabled={item.check}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-700">{formatDate(item.depositdate)}</td>
                      <td className="px-3 py-2 text-gray-700">{item.name || "-"}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(item.incash)}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleExcludeBankItem(item)}
                          disabled={processing || excludingId === item.id || !selectedBankIds.has(item.id)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${
                            selectedBankIds.has(item.id)
                              ? "bg-emerald-600 text-white hover:bg-emerald-700"
                              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                          }`}
                        >
                          対象外
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-gray-100 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">消込一覧</h3>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "normal", label: "消込一覧（通常）" },
                  { key: "trouble", label: "消込（トラブル）" },
                ] as const
              ).map((tab) => {
                const isActive = paymentTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setPaymentTab(tab.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                      isActive
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-gray-500">読み込み中...</div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">未消込のpaymentsがありません。</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-sky-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">期日</th>
                    <th className="px-3 py-2 text-left">注文番号</th>
                    <th className="px-3 py-2 text-left">氏名</th>
                    <th className="px-3 py-2 text-right">予定額</th>
                    <th className="px-3 py-2 text-center">回数</th>
                    <th className="px-3 py-2 text-left">支払方法</th>
                    <th className="px-3 py-2 text-center">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPayments.map((payment) => {
                    const isPendingConfirm = confirmingPayment?.paymentRefPath === payment.paymentRefPath;
                    const isSelectionReady = selectedBankIds.size > 0;
                    const buttonClass = isPendingConfirm
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : isSelectionReady
                        ? "bg-white text-indigo-600 border-indigo-600 hover:bg-indigo-50"
                        : "bg-gray-200 text-gray-400 border-gray-200";
                    return (
                    <tr key={payment.paymentRefPath} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{formatDate(payment.dueDate)}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {payment.orderId !== "-" ? (
                          <Link to={`/admin/orders/${payment.orderId}`} className="text-emerald-600 hover:underline">
                            {payment.orderNumber}
                          </Link>
                        ) : (
                          payment.orderNumber
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {payment.userId !== "-" ? (
                          <Link to={`/admin/users/${payment.userId}`} className="text-emerald-600 hover:underline">
                            {payment.userName}
                          </Link>
                        ) : (
                          payment.userName
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(payment.amount)}</td>
                      <td className="px-3 py-2 text-center text-gray-700">
                        {payment.installmentNumber ?? "-"}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{payment.paymentMethod}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => requestMatchPayment(payment)}
                            disabled={processing || !isSelectionReady}
                            className={`rounded-full border px-4 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${buttonClass}`}
                          >
                            消込
                          </button>
                          {payment.paymentMethod === "コンビニ決済" && (
                            <button
                              type="button"
                              onClick={() => handleKmjMatch(payment)}
                              disabled={processing}
                              className="rounded-full border border-indigo-600 px-4 py-1 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-60"
                            >
                              KMJ消
                            </button>
                          )}
                          {adminMode && (
                            <button
                              type="button"
                              onClick={() => handleDeletePayment(payment)}
                              disabled={processing}
                              className="rounded-full border border-red-500 px-4 py-1 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                            >
                              削除
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirmingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h4 className="text-lg font-semibold text-gray-800">消込を実行しますか？</h4>
            <p className="mt-2 text-sm text-gray-600">
              選択した入金データで「{confirmingPayment.orderNumber}」の消込を行います。
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmingPayment(null)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                disabled={processing}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleConfirmMatch}
                disabled={processing}
                className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
